/**
 * Rep GPS Routes (2026-10-19) — where and when a rep drove, for Blue Mango's managers: the shifts the Android Rep
 * Tracker app uploaded (backend /api/v1/field-track, requireMaster: Michael + Helena only; shown under Reps while
 * Blue Mango is the organisation in view). Record: backend docs/michael/rep-field/IMPL-rep-tracker-app.md §18–19.
 *
 * Michael: "on the left it shows each checkpoint the rep was at (in order) … click it to see the highlighted route …
 * view full route … indicators on the map for each checkpoint (with name, timestamp) … if the rep hasn't moved that
 * shouldn't be recorded … verify they didn't drive to other places in between their stops."
 *
 * Left: pick a day (shifts with no real driving hidden), then the ITINERARY — start → drive → stop → drive → … → end.
 * Every place is named server-side (store within 150 m › rep's home › street). A drive shows km, time, top speed,
 * and anything suspicious inside it: brief halts (stood still 1–3 min), GPS holes, location switched off.
 * Click a drive: only that leg is highlighted and the map zooms to it. Click a stop: the map zooms to it.
 * "Full route": the whole day, every stop numbered with its name and times.
 *
 * Leaflet is plain (no react-leaflet), the same way as Map View: one map in a ref, layers rebuilt on change.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import axios from "axios"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { Badge, Box, Button, Flex, Input, Select, Spinner, Switch, Text } from "@chakra-ui/react"
import PageToolbar from "../../components/shared/PageToolbar"
import { Panel } from "../../components/shared/Section"
import { brand, neutral, radius, signal, type } from "../../theme/tokens"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"

const API = process.env.REACT_APP_API_URL
const uid = () => localStorage.getItem("userid")
const post = (path, body = {}) => axios.post(`${API}/field-track/${path}`, { userid: uid(), ...body })

const TZ = "Africa/Johannesburg"
const hhmm = (t) => new Date(t).toLocaleTimeString("en-ZA", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false })
const day = (t) => new Date(t).toLocaleDateString("en-ZA", { timeZone: TZ, weekday: "short", day: "numeric", month: "short" })
const sameDay = (a, b) => day(a) === day(b)
const hhmmOn = (t, ref) => (sameDay(t, ref) ? hhmm(t) : `${day(t)} ${hhmm(t)}`)
const ymd = (d) => new Date(d.getTime() + 2 * 3600e3).toISOString().slice(0, 10)
const dur = (min) => (min >= 60 ? `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, "0")}` : `${min} min`)
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]))
const short = (s, n = 34) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "")

// A shift with less driving than this is a phone that never left (or a test) — hidden unless asked for.
const MIN_KM = 2

// Speed bands (km/h) — a sequential ramp, red only for genuinely fast driving.
const BANDS = [
  { max: 40, color: brand[300], label: "< 40" },
  { max: 80, color: brand[500], label: "40–80" },
  { max: 120, color: brand[800], label: "80–120" },
  { max: Infinity, color: signal.down, label: "> 120 km/h" },
]
const bandOf = (kmh) => BANDS.findIndex((b) => kmh < b.max)
const DIM = neutral[400]

const ISSUE_TEXT = {
  LOCATION_OFF: "Location switched OFF", SERVICE_KILLED: "Android stopped the app", MOCK_LOCATION: "FAKE GPS app detected",
  PERMISSION_LOST: "Location permission removed", GPS_STALE: "GPS went quiet", WATCHDOG_RESTART: "App restarted itself",
  BOOT_RESUME: "Phone restarted", LOW_BATTERY: "Low battery",
}
const LOG_TEXT = {
  SHIFT_START: "Shift started", SHIFT_END: "Shift ended", AUTO_CLOSE: "Shift ended automatically (23:59)",
  TASK_REMOVED: "App swiped away — still recording", LOCATION_ON: "Location switched on", SERVICE_START: "Tracking running",
  GPS_FALLBACK: "Switched to raw GPS", ...ISSUE_TEXT,
}
const HIDDEN_EVENTS = new Set(["UPLOAD_OK", "UPLOAD_FAIL"])
const SOFT_ISSUES = new Set(["LOW_BATTERY"])

const placeOf = (it) => it?.place?.label || "—"
const PlaceBadge = ({ place }) => {
  if (!place) return null
  if (place.kind === "store") return <Badge colorScheme="teal" variant="subtle" fontSize="0.65rem">store</Badge>
  if (place.kind === "home") return <Badge colorScheme="purple" variant="subtle" fontSize="0.65rem">home</Badge>
  return null
}

const Stat = ({ label, value, hint }) => (
  <Box minW="104px">
    <Text fontSize={type.size.micro} letterSpacing={type.tracking.label} textTransform="uppercase" color={neutral[500]} fontWeight={type.weight.semibold}>{label}</Text>
    <Text fontSize={type.size.title} fontWeight={type.weight.semibold} color={neutral[900]} lineHeight="1.3">{value}</Text>
    {hint && <Text fontSize={type.size.caption} color={neutral[500]}>{hint}</Text>}
  </Box>
)

const GpsRoutes = () => {
  const { isMaster } = useSupplierRetailer()
  if (!isMaster) return <Text p={6} color={neutral[500]}>This page is for the SpearBI masters only.</Text>
  return <GpsRoutesPage />
}

const GpsRoutesPage = () => {
  const [from, setFrom] = useState(() => ymd(new Date(Date.now() - 7 * 86400e3)))
  const [to, setTo] = useState(() => ymd(new Date()))
  const [shifts, setShifts] = useState(null)
  const [showIdle, setShowIdle] = useState(false)
  const [error, setError] = useState(null)
  const [sel, setSel] = useState(null)
  const [detail, setDetail] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [focus, setFocus] = useState(null) // null = full route; else an itinerary index
  const [cursor, setCursor] = useState(null)
  const [showLog, setShowLog] = useState(false)

  const mapEl = useRef(null)
  const mapRef = useRef(null)
  const layerRef = useRef(null)
  const cursorRef = useRef(null)
  const markerRefs = useRef({})

  const visibleShifts = useMemo(() => (shifts || []).filter((s) => showIdle || s.km >= MIN_KM), [shifts, showIdle])
  const hiddenCount = (shifts || []).length - visibleShifts.length

  useEffect(() => {
    setShifts(null); setError(null)
    post("shifts", { from, to })
      .then((r) => setShifts(r.data.data || []))
      .catch((e) => setError(e?.response?.data?.message || e.message))
  }, [from, to])

  // Keep the chosen shift if it is still listed, else open the newest real one.
  useEffect(() => {
    if (!shifts) return
    setSel((cur) => (cur && visibleShifts.some((s) => s.shift_id === cur) ? cur : visibleShifts[0]?.shift_id || null))
  }, [shifts, visibleShifts])

  useEffect(() => {
    if (!sel) { setDetail(null); return }
    let live = true
    setLoadingDetail(true); setFocus(null); setCursor(null)
    post("shift", { shift_id: sel })
      .then((r) => { if (live) setDetail(r.data.data) })
      .catch((e) => live && setError(e?.response?.data?.message || e.message))
      .finally(() => live && setLoadingDetail(false))
    return () => { live = false }
  }, [sel])

  /* ---------- leaflet lifecycle ---------- */
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return
    const map = L.map(mapEl.current, { zoomSnap: 0.5 }).setView([-29.2, 25.5], 5.5)
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}", {
      attribution: "Tiles &copy; Esri &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors", maxZoom: 19,
    }).addTo(map)
    mapRef.current = map
    return () => { map.remove(); mapRef.current = null }
  }, [])

  const items = useMemo(() => detail?.itinerary || [], [detail])
  const stopNo = useMemo(() => items.filter((i) => i.kind === "stop").length, [items])

  // A drive's line as runs of one speed band; parked and fuzzy (> ±50 m) fixes are not drawn, a > 2 min hole breaks it.
  const runsOf = useCallback((i0, i1) => {
    const out = []
    let cur = null, prev = null
    for (let i = i0; i <= i1; i++) {
      const p = detail.points[i]
      if (!(p.mv && !p.flag && (p.acc == null || p.acc <= 50))) { prev = null; continue }
      const b = bandOf((p.spd || 0) * 3.6)
      if (!cur || !prev || p.t - prev.t > 120e3 || b !== cur.band) {
        cur = { band: b, pts: prev && p.t - prev.t <= 120e3 ? [[prev.lat, prev.lng]] : [] }
        out.push(cur)
      }
      cur.pts.push([p.lat, p.lng])
      prev = p
    }
    return out.filter((r) => r.pts.length > 1)
  }, [detail])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (layerRef.current) { layerRef.current.remove(); layerRef.current = null }
    markerRefs.current = {}
    if (!detail || !detail.points.length) return
    const g = L.layerGroup().addTo(map)
    const focused = focus != null ? items[focus] : null
    const bounds = []

    items.forEach((it, idx) => {
      if (it.kind !== "drive") return
      const on = !focused || focus === idx
      for (const r of runsOf(it.i0, it.i1)) {
        L.polyline(r.pts, on ? { color: BANDS[r.band].color, weight: focused ? 6 : 4, opacity: 0.95 } : { color: DIM, weight: 3, opacity: 0.45 }).addTo(g)
        if (on) bounds.push(...r.pts)
      }
      if (on) for (const h of it.halts || []) {
        L.circleMarker([h.lat, h.lng], { radius: 6, color: neutral[0], weight: 2, fillColor: signal.down, fillOpacity: 1 })
          .bindTooltip(`Halt ${dur(h.minutes)} · ${hhmm(h.from)}<br>${esc(h.place?.label || "")}`, { direction: "top" }).addTo(g)
        bounds.push([h.lat, h.lng])
      }
    })

    items.forEach((it, idx) => {
      if (it.kind === "drive") return
      const near = !focused || Math.abs(idx - focus) === 1 || focus === idx
      const isStop = it.kind === "stop"
      const label = isStop ? String(it.n) : it.kind === "start" ? "S" : "E"
      const color = isStop ? (it.place?.kind === "store" ? brand[600] : signal.warn) : it.kind === "start" ? signal.up : neutral[900]
      const time = isStop ? `${hhmmOn(it.arrived, detail.points[0].t)} – ${hhmmOn(it.left, detail.points[0].t)} (${dur(it.minutes)})` : hhmm(it.t)
      const icon = L.divIcon({
        className: "",
        iconSize: [26, 26], iconAnchor: [13, 13],
        html: `<div style="width:26px;height:26px;border-radius:50%;background:${near ? color : DIM};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.35);color:#fff;font:700 12px/22px Inter,sans-serif;text-align:center">${label}</div>`,
      })
      const m = L.marker([it.lat, it.lng], { icon, zIndexOffset: near ? 1000 : 0 }).addTo(g)
      m.bindPopup(`<b>${isStop ? `Stop ${it.n}` : it.kind === "start" ? "Start" : "End"}</b> · ${esc(time)}<br>${esc(placeOf(it))}` +
        (isStop ? `<br><span style="color:${neutral[500]}">position ±${it.accuracy_m} m${it.fuzzy ? " (indoors — no GPS fix)" : ""}</span>` : ""))
      // labels alternate sides so stops close together (a store, then home round the corner) do not cover each other
      const side = (isStop ? it.n : it.kind === "start" ? 0 : stopNo + 1) % 2 === 0 ? "left" : "right"
      if (near) m.bindTooltip(`<b>${esc(short(placeOf(it), 28))}</b><br>${esc(time)}`, { permanent: !focused || focus === idx || Math.abs(idx - focus) === 1, direction: side, offset: [side === "right" ? 14 : -14, 0], className: "gps-place-tip" })
      markerRefs.current[idx] = m
      if (near) bounds.push([it.lat, it.lng])
    })

    layerRef.current = g
    if (focused && focused.kind !== "drive") map.setView([focused.lat, focused.lng], Math.max(map.getZoom(), 16))
    else if (bounds.length) map.fitBounds(L.latLngBounds(bounds), { padding: [40, 40], maxZoom: 16 })
  }, [detail, items, focus, runsOf, stopNo])

  // The time slider's marker: the fix nearest to the chosen moment.
  const atCursor = useMemo(() => {
    if (!detail || cursor == null || !detail.points.length) return null
    const ps = detail.points
    let lo = 0, hi = ps.length - 1
    while (lo < hi) { const m = (lo + hi) >> 1; if (ps[m].t < cursor) lo = m + 1; else hi = m }
    const p = lo > 0 && Math.abs(ps[lo - 1].t - cursor) < Math.abs(ps[lo].t - cursor) ? ps[lo - 1] : ps[lo]
    return { p, offMin: Math.round(Math.abs(p.t - cursor) / 60e3) }
  }, [detail, cursor])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (cursorRef.current) { cursorRef.current.remove(); cursorRef.current = null }
    if (!atCursor) return
    cursorRef.current = L.circleMarker([atCursor.p.lat, atCursor.p.lng], { radius: 10, color: brand[700], weight: 3, fillColor: neutral[0], fillOpacity: 1 }).addTo(map)
  }, [atCursor])

  const log = useMemo(() => (detail?.events || []).filter((e) => !HIDDEN_EVENTS.has(e.type) && e.type !== "MOTION_CHANGE"), [detail])

  const shift = detail?.shift
  const t0 = detail?.points?.[0]?.t
  const first = detail?.points?.[0], last = detail?.points?.[detail.points.length - 1]
  const driveMin = items.filter((i) => i.kind === "drive").reduce((a, i) => a + i.minutes - (i.holes || []).reduce((h, g) => h + g.minutes, 0), 0)
  const flags = items.reduce((n, i) => n + (i.halts?.length || 0) + (i.holes?.length || 0) + (i.issues || []).filter((x) => !SOFT_ISSUES.has(x.type)).length, 0)

  const ItemRow = ({ it, idx }) => {
    const active = focus === idx
    const base = {
      px: 4, py: 2.5, cursor: "pointer", borderLeft: "3px solid", borderLeftColor: active ? brand[500] : "transparent",
      bg: active ? brand[50] : "transparent", _hover: { bg: active ? brand[50] : neutral[25] }, onClick: () => setFocus(active ? null : idx),
    }
    if (it.kind === "drive") {
      const hole = (it.holes || []).reduce((a, g) => a + g.minutes, 0)
      const issues = (it.issues || []).filter((x) => !SOFT_ISSUES.has(x.type))
      return (
        <Flex {...base} gap={3} pl={4}>
          <Box w="26px" flexShrink={0} display="flex" justifyContent="center"><Box w="3px" bg={active ? brand[500] : neutral[300]} borderRadius="2px" minH="34px" /></Box>
          <Box flex="1" minW={0}>
            <Text fontSize={type.size.small} color={neutral[800]} fontWeight={type.weight.semibold}>
              Drive {it.km} km · {dur(Math.max(0, it.minutes - hole))}
              <Text as="span" color={neutral[500]} fontWeight={type.weight.regular}> · {hhmm(it.from)}–{hhmmOn(it.to, it.from)}{it.top_kmh ? ` · top ${it.top_kmh} km/h` : ""}</Text>
            </Text>
            {(it.halts || []).map((h, k) => (
              <Text key={`h${k}`} fontSize={type.size.caption} color={signal.down}>⚑ stood still {dur(h.minutes)} at {hhmm(h.from)} — {short(h.place?.label, 40)}</Text>
            ))}
            {(it.holes || []).map((g, k) => (
              <Text key={`g${k}`} fontSize={type.size.caption} color={signal.down}>⚠ no GPS for {dur(g.minutes)} ({hhmmOn(g.from, it.from)} → {hhmmOn(g.to, it.from)})</Text>
            ))}
            {issues.map((x, k) => (
              <Text key={`i${k}`} fontSize={type.size.caption} color={signal.down}>⚠ {ISSUE_TEXT[x.type] || x.type} at {hhmm(x.t)}</Text>
            ))}
          </Box>
        </Flex>
      )
    }
    const isStop = it.kind === "stop"
    const dot = isStop ? (it.place?.kind === "store" ? brand[600] : signal.warn) : it.kind === "start" ? signal.up : neutral[900]
    const title = isStop ? `${hhmmOn(it.arrived, t0)} – ${hhmmOn(it.left, t0)}` : `${it.kind === "start" ? "Start" : "End"} · ${hhmmOn(it.t, t0)}`
    return (
      <Flex {...base} gap={3} align="flex-start">
        <Flex w="26px" h="26px" flexShrink={0} borderRadius="50%" bg={dot} color="#fff" align="center" justify="center" fontSize="12px" fontWeight={700} boxShadow="0 1px 2px rgba(0,0,0,.2)">
          {isStop ? it.n : it.kind === "start" ? "S" : "E"}
        </Flex>
        <Box flex="1" minW={0}>
          <Flex gap={2} align="center" wrap="wrap">
            <Text fontSize={type.size.body} fontWeight={type.weight.semibold} color={neutral[900]} noOfLines={2}>{placeOf(it)}</Text>
            <PlaceBadge place={it.place} />
          </Flex>
          <Text fontSize={type.size.caption} color={neutral[600]}>
            {title}{isStop ? ` · ${dur(it.minutes)}` : ""}{it.is_start ? " · shift started here" : ""}{it.is_end ? " · shift ended here" : ""}
          </Text>
          {isStop && it.fuzzy && <Text fontSize={type.size.caption} color={neutral[500]}>indoors — position ±{it.accuracy_m} m</Text>}
          {(it.issues || []).filter((x) => !SOFT_ISSUES.has(x.type)).map((x, k) => (
            <Text key={k} fontSize={type.size.caption} color={signal.down}>⚠ {ISSUE_TEXT[x.type] || x.type} at {hhmm(x.t)}</Text>
          ))}
        </Box>
      </Flex>
    )
  }

  return (
    <Box>
      <style>{`.gps-place-tip{font:12px/1.35 Inter,sans-serif;padding:3px 7px;border-radius:6px;border:1px solid ${neutral[200]};box-shadow:0 1px 3px rgba(15,30,40,.15)}`}</style>
      <PageToolbar
        description="Where and when each rep drove — from the Rep Tracker app."
        filters={
          <>
            <Text fontSize={type.size.small} color={neutral[500]}>From</Text>
            <Input type="date" size="sm" w="150px" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
            <Text fontSize={type.size.small} color={neutral[500]}>to</Text>
            <Input type="date" size="sm" w="150px" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} />
          </>
        }
      />
      {error && <Text color={signal.down} mb={3} fontSize={type.size.small}>{error}</Text>}

      <Flex gap={4} direction={{ base: "column", lg: "row" }} align="stretch">
        {/* ---- left: the day + its itinerary ---- */}
        <Panel padded={false} w={{ base: "100%", lg: "380px" }} flexShrink={0} display="flex" flexDirection="column" maxH={{ lg: "calc(100vh - 190px)" }}>
          <Box p={3} borderBottom="1px solid" borderColor={neutral[200]}>
            {!shifts && !error && <Flex py={2} justify="center"><Spinner size="sm" color={brand[500]} /></Flex>}
            {shifts && !visibleShifts.length && (
              <Text fontSize={type.size.small} color={neutral[500]}>No driving recorded in these dates.</Text>
            )}
            {visibleShifts.length > 0 && (
              <Select size="sm" value={sel || ""} onChange={(e) => setSel(e.target.value)}>
                {visibleShifts.map((s) => (
                  <option key={s.shift_id} value={s.shift_id}>
                    {day(s.started_at)} · {hhmm(s.started_at)}–{s.ended_at ? hhmmOn(s.ended_at, s.started_at) : "open"} · {s.km} km · {s.rep_name || s.app_rep_id || s.device_label}
                  </option>
                ))}
              </Select>
            )}
            <Flex mt={2} align="center" justify="space-between" gap={2}>
              <Button size="xs" variant={focus == null ? "solid" : "outline"} colorScheme="teal" onClick={() => setFocus(null)} isDisabled={!detail}>
                Full route
              </Button>
              {shifts && (hiddenCount > 0 || showIdle) && (
                <Flex align="center" gap={2} fontSize={type.size.caption} color={neutral[500]}>
                  show {hiddenCount || ""} without driving <Switch size="sm" isChecked={showIdle} onChange={(e) => setShowIdle(e.target.checked)} />
                </Flex>
              )}
            </Flex>
          </Box>
          <Box flex="1" overflowY="auto" py={1}>
            {loadingDetail && <Flex p={6} justify="center"><Spinner color={brand[500]} /></Flex>}
            {!loadingDetail && items.map((it, idx) => <ItemRow key={idx} it={it} idx={idx} />)}
          </Box>
        </Panel>

        {/* ---- right: summary, map, slider, phone log ---- */}
        <Flex direction="column" gap={4} flex="1" minW={0}>
          <Panel>
            {shift ? (
              <Flex gap={6} wrap="wrap">
                <Stat label="Driving" value={`${shift.km} km`} hint={dur(driveMin)} />
                <Stat label="Stops" value={stopNo} hint="3 min or longer" />
                <Stat label="Day" value={`${hhmm(shift.started_at)} – ${shift.ended_at ? hhmmOn(shift.ended_at, shift.started_at) : "open"}`} hint={`${day(shift.started_at)} · ${shift.rep_name || shift.app_rep_id || ""}`} />
                <Stat label="To check" value={flags} hint={flags ? "halts, GPS holes, location off" : "nothing unusual"} />
                <Stat label="Battery" value={detail.battery ? `${detail.battery.from}% → ${detail.battery.to}%` : "—"} hint={shift.device_label} />
              </Flex>
            ) : (
              <Text fontSize={type.size.small} color={neutral[500]}>{shifts && visibleShifts.length ? "Loading…" : "—"}</Text>
            )}
          </Panel>

          <Panel padded={false} position="relative">
            <Box ref={mapEl} h={{ base: "440px", lg: "600px" }} w="100%" />
            {loadingDetail && (
              <Flex position="absolute" inset={0} align="center" justify="center" bg="rgba(255,255,255,0.6)" zIndex={500}><Spinner color={brand[500]} /></Flex>
            )}
            <Flex position="absolute" bottom={3} left={3} zIndex={500} bg={neutral[0]} borderRadius={radius.sm} px={3} py={2} gap={3} wrap="wrap"
              boxShadow="0 1px 4px rgba(15,30,40,0.15)" fontSize={type.size.caption} color={neutral[600]}>
              {BANDS.map((b) => (
                <Flex key={b.label} align="center" gap={1.5}><Box w="16px" h="4px" bg={b.color} borderRadius="2px" />{b.label}</Flex>
              ))}
              <Flex align="center" gap={1.5}><Box w="10px" h="10px" bg={brand[600]} borderRadius="50%" />store</Flex>
              <Flex align="center" gap={1.5}><Box w="10px" h="10px" bg={signal.warn} borderRadius="50%" />other stop</Flex>
              <Flex align="center" gap={1.5}><Box w="10px" h="10px" bg={signal.down} borderRadius="50%" />halt</Flex>
            </Flex>
          </Panel>

          {first && last && (
            <Panel>
              <Flex align="center" gap={3} wrap="wrap">
                <Text fontSize={type.size.small} color={neutral[600]} minW="160px">
                  {atCursor ? `${hhmmOn(atCursor.p.t, t0)} · ${atCursor.p.mv ? `driving ${Math.round((atCursor.p.spd || 0) * 3.6)} km/h` : "parked"}${atCursor.offMin > 2 ? ` (nearest fix ${atCursor.offMin} min away)` : ""}` : "Where was the rep at …"}
                </Text>
                <input type="range" min={first.t} max={last.t} step={60000} value={cursor ?? first.t}
                  onChange={(e) => setCursor(Number(e.target.value))} style={{ flex: 1, minWidth: 200, accentColor: brand[500] }} />
              </Flex>
            </Panel>
          )}

          {detail && (
            <Panel padded={false}>
              <Flex px={4} py={3} justify="space-between" align="center" cursor="pointer" onClick={() => setShowLog((v) => !v)}>
                <Text fontSize={type.size.body} fontWeight={type.weight.semibold} color={neutral[800]}>Phone log ({log.length})</Text>
                <Text fontSize={type.size.caption} color={brand[500]}>{showLog ? "hide" : "show"}</Text>
              </Flex>
              {showLog && log.map((e, i) => (
                <Flex key={i} px={4} py={2} gap={3} borderTop="1px solid" borderColor={neutral[100]}>
                  <Text fontSize={type.size.data} color={neutral[500]} w="92px" flexShrink={0} sx={{ fontVariantNumeric: "tabular-nums" }}>{hhmmOn(e.t, t0)}</Text>
                  <Text fontSize={type.size.small} color={ISSUE_TEXT[e.type] && !SOFT_ISSUES.has(e.type) ? signal.down : neutral[700]}>
                    {LOG_TEXT[e.type] || e.type}{e.detail && !["user", "start"].includes(e.detail) ? ` (${e.detail})` : ""}
                  </Text>
                </Flex>
              ))}
            </Panel>
          )}
        </Flex>
      </Flex>
    </Box>
  )
}

export default GpsRoutes
